import React, { Fragment } from "react";
import { useSearchParams } from "next/navigation";

import getStrain from "@/api/requests/getStrain";
import getStrainPhotos from "@/api/requests/getStrainPhotos";
import { ads } from "@/constants/ads";
import { Photo } from "@/custom-types/Strain";
import { StrainV2 } from "@/custom-types/StrainV2";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { throwOnRedirectHint } from "@/utils/redirectHints";
import { redirectIfPageOne } from "@/utils/redirectIfPageOne";

import Button from "@/components/botanic/Button";
import PhotoGallery from "@/components/botanic/PhotoGallery";
import Breadcrumb from "@/components/Breadcrumb";
import Breadcrumbs from "@/components/Breadcrumbs";
import GoogleAd from "@/components/GoogleAd";
import MetaTags from "@/components/MetaTags";
import PageNavigationV2 from "@/components/PageNavigation";

const EAGER_LOAD_COUNT = 3;

const PHOTOS_PER_PAGE = 36;

type PhotosProps = {
  strain: StrainV2;
  photos: Photo[];
  totalCount: number;
};

const Photos: React.FC<PhotosProps> = ({ strain, photos, totalCount }) => {
  const page = useSearchParams()?.get("page");
  const currentPage = page ? Number(page) : 1;

  const ogImageUrl =
    photos[0]?.imageUrl &&
    `${photos[0].imageUrl}?auto=compress&w=1200&h=630&fit=crop`;

  return (
    <Fragment>
      <MetaTags
        description={`See photos of ${strain.name} cannabis buds. Browse user-submitted photos of ${strain.name} weed and upload your own images of this marijuana strain.`}
        image={ogImageUrl}
        title={`Photos of ${strain.name} Weed Strain Buds | Leafly`}
        {...(photos?.length === 0 && { robots: "noindex" })}
      />

      <div className="container pt-xxl">
        <h1 className="mb-sm">{`${strain.name} photos`}</h1>
        {photos.length !== 0 && (
          <p>
            People shared these photos of the cannabis strain {strain.name}.
          </p>
        )}
        <div className="gam-rectangular-fixed-height my-xxl">
          <GoogleAd
            adPath={ads.strainPhotosUpper.path}
            targeting={{ strain: strain.slug }}
            sizes={{ desktop: [728, 90], mobile: [320, 50] }}
          />
        </div>

        <PhotoGallery
          images={photos}
          emptyStateMessage="No photos of this strain yet."
          eagerLoadWhen={(_image: string, i: number) => i < EAGER_LOAD_COUNT}
        />
        {photos.length === 0 && (
          <Button href={`/strains/${strain.slug}`} className="mb-xxl mx-auto">
            back to {strain.name} strain
          </Button>
        )}

        {photos.length !== 0 && (
          <div className="gam-square-fixed-height mt-xl">
            <GoogleAd
              adPath={ads.strainPhotosLower.path}
              targeting={{ strain: strain.slug }}
              sizes={{ desktop: [300, 250], mobile: [300, 250] }}
            />
          </div>
        )}
        <PageNavigationV2
          className="mt-xl"
          currentPage={currentPage}
          totalPages={Math.ceil(totalCount / PHOTOS_PER_PAGE)}
        />
      </div>

      <Breadcrumbs>
        <Breadcrumb href="/strains"> Weed Strains </Breadcrumb>
        <Breadcrumb
          href={
            strain.phenotype
              ? `/strains/lists/category/${strain.phenotype.toLowerCase()}`
              : `/strains/${strain.slug}`
          }
        >
          {strain.phenotype ? strain.phenotype : strain.name}
        </Breadcrumb>
        <Breadcrumb href={`/strains/${strain.slug}`}>{strain.name}</Breadcrumb>
        <Breadcrumb currentLocation href={`/strains/${strain.slug}/photos`}>
          {strain.name} Photos
        </Breadcrumb>
      </Breadcrumbs>
    </Fragment>
  );
};

export const getServerSideProps = createGetServerSideProps<PhotosProps>(
  async (context) => {
    const { query, resolvedUrl } = context;
    const slug = Array.isArray(query?.strainSlug)
      ? query.strainSlug[0]
      : query.strainSlug || "";

    const redirect = redirectIfPageOne(resolvedUrl, query.page);

    if (redirect) {
      return { redirect };
    }

    const strain = await throwOnRedirectHint(() => getStrain(slug), slug);

    if (!strain) {
      return {
        notFound: true,
      };
    }

    const page = Array.isArray(query.page)
      ? +query.page[0]
      : +(query.page || 1);
    const {
      metadata: { totalCount },
      data: photos,
    } = await getStrainPhotos(strain.slug, page, PHOTOS_PER_PAGE);

    if (page > 1 && page > Math.ceil(totalCount / PHOTOS_PER_PAGE)) {
      return {
        notFound: true,
      };
    }

    return {
      props: { photos, strain, totalCount },
    };
  },
);

export default Photos;
